import { signMessage } from '@wagmi/core'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { STUDIO_BOT } from '@/constants/blockchain'
import { useAuth } from '@/plugins/auth'
import { getWagmiConfig } from '@/plugins/auth/config'
import { CustomError } from '@/utils/helpers'
import { convertTimestampDateAndTimeUTC, post } from '@/utils/utils'

// Request / Response types

// Unauthenticated routes

// Token
// POST /auth/user
type AuthTokenRequest = {
  address: string
  username: string
  timestamp: string
  signature: string
}

type AuthTokenResponse = {
  valid: boolean
  server_address: string
  token: string
  expiry: string
  error?: string
}

const keyName = (address: string) => `spacesUserAuthInfo-${address}`

const isTokenExpired = (authInfo: string): boolean => {
  try {
    const parsedInfo = JSON.parse(authInfo)
    const expiry = new Date(`${parsedInfo.expiry}Z`)
    const now = new Date()
    return now > expiry
  } catch (error) {
    return true
  }
}

// Handle getting User auth token from Spaces Server
const useSpacesAuth = () => {
  // Hooks
  const { address, userIdentity } = useAuth()

  const username = useMemo(
    () => (userIdentity?.username ? userIdentity.username : ''),
    [userIdentity]
  )

  // Keep token in state
  const [token, setToken] = useState<string>('')

  // Dedup requests
  const requestedToken = useRef<boolean>(false)
  // Error
  const [error, setError] = useState<CustomError>({
    call: 'spaces.auth.user',
    msg: '',
    isError: false,
  })

  // Request data
  const timestamp = useMemo(() => convertTimestampDateAndTimeUTC(new Date()), [])
  const tokenMessage = useMemo(
    () => `I, ${username || address?.toLowerCase()}, want to access LAMINA1 Hub on ${timestamp}`,
    [username, address, timestamp]
  )

  // Get a new auth token from the server
  const fetchAuthToken = useCallback(async () => {
    if (!address) return

    // Get signature first
    let signature: string | undefined
    try {
      signature = await signMessage(getWagmiConfig(), { message: tokenMessage })
    } catch (err: any) {
      setError({ call: 'spaces.auth.user', msg: 'Failed to get signature', isError: true })
      return
    }
    if (!signature) {
      setError({ call: 'spaces.auth.user', msg: 'Failed to get signature', isError: true })
      return
    }
    // Make request
    try {
      const req: AuthTokenRequest = {
        address,
        username,
        timestamp,
        signature,
      }
      const retval = (await post(
        `${STUDIO_BOT}/auth/user`,
        JSON.stringify(req)
      )) as AuthTokenResponse
      // Set error if response not valid
      if (!retval.valid) {
        setError({
          call: 'spaces.auth.user',
          msg: retval.error || 'Error connecting to Spaces Server',
          isError: true,
        })
      } else {
        // Get token from response, build auth info and save to local storage
        const authInfo = JSON.stringify({
          token: retval.token,
          expiry: retval.expiry,
        })
        // Set token
        setToken(retval.token)
        // Save to local storage
        window.localStorage.setItem(keyName(address), authInfo)
      }
    } catch (err: any) {
      setError({ call: 'spaces.auth.user', msg: err.message, isError: true })
    }
  }, [address, username, timestamp])

  // Fetch token if needed
  useEffect(() => {
    // Clear token when address changes to empty
    if (!address) {
      setToken('')
    } else {
      // Get auth info from local storage
      const authInfo = window.localStorage.getItem(keyName(address))
      // Fetch token if not found or expired (only once)
      if ((!authInfo || isTokenExpired(authInfo)) && !requestedToken.current) {
        console.log('Fetching auth token')
        // Fetch token
        fetchAuthToken().catch(() => {
          requestedToken.current = false
        })
        requestedToken.current = true
      } else if (authInfo) {
        // Set token from local storage
        setToken(JSON.parse(authInfo).token)
      }
    }
  }, [address, requestedToken])

  return {
    token,
    error,
    fetchAuthToken,
  }
}

export default useSpacesAuth
